import { getSession } from '@/auth/session';
import { ApiError, NetworkError, problemFrom } from './errors';

export const API_BASE = '/api/v1';

// Polling must not keep an unattended session alive. Navigation/typing can mark a nearby read as activity.
let lastInteractionAt = Date.now();
if (typeof window !== 'undefined') {
  const interacted = () => { lastInteractionAt = Date.now(); };
  window.addEventListener('pointerdown', interacted, { passive: true });
  window.addEventListener('keydown', interacted, { passive: true });
  window.addEventListener('scroll', interacted, { passive: true, capture: true });
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  query?: Query;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Login itself must not trigger the re-authentication dialog. */
  anonymous?: boolean;
}

/**
 * When a request answers 401 during work, the app asks the person to log in again in place (keeping what they
 * were typing) and the request is retried once. Registered by the ReauthDialog; without it, go to the login page.
 */
let reauthenticate: (() => Promise<boolean>) | null = null;
let pendingReauth: Promise<boolean> | null = null;

export function registerReauthentication(handler: (() => Promise<boolean>) | null): void {
  reauthenticate = handler;
}

function url(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query)
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return API_BASE + path + (s ? `?${s}` : '');
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const read = (opts.method === undefined || opts.method === 'GET') && opts.body === undefined;
  if (read && Date.now() - lastInteractionAt > 5_000) headers['X-Session-Activity'] = 'passive';
  const session = getSession();
  if (session && !opts.anonymous) headers.Authorization = `Bearer ${session.token}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  try {
    return await fetch(url(path, opts.query), {
      method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
      headers,
      body,
      signal: opts.signal,
      credentials: 'same-origin',
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new NetworkError();
  }
}

async function authorized(path: string, opts: RequestOptions): Promise<Response> {
  let response = await send(path, opts);
  if (response.status === 401 && !opts.anonymous) {
    if (!reauthenticate) {
      redirectToLogin();
      throw await problemFrom(response);
    }
    pendingReauth ??= reauthenticate().finally(() => (pendingReauth = null));
    if (await pendingReauth) response = await send(path, opts);
  }
  return response;
}

export function redirectToLogin(): void {
  const next = window.location.pathname + window.location.search;
  if (!window.location.pathname.startsWith('/ingresar'))
    window.location.assign(`/ingresar?next=${encodeURIComponent(next)}`);
}

/** JSON request. Endpoints that answer a JSON document as text (quote issue/accept) are parsed the same way. */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const response = await authorized(path, opts);
  if (!response.ok) throw await problemFrom(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  try {
    const parsed: unknown = JSON.parse(text);
    // A String body with produces=application/json can arrive JSON-encoded twice.
    return (typeof parsed === 'string' && /^[[{]/.test(parsed) ? JSON.parse(parsed) : parsed) as T;
  } catch {
    throw new ApiError(response.status, 'INVALID_RESPONSE', 'Respuesta inesperada del servidor');
  }
}

/** Read bounded server pages without silently dropping the remaining rows in existing selectors. */
export async function apiPages<T>(path: string, opts: RequestOptions = {}): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 0; ; page++) {
    const batch = await api<T[]>(path, { ...opts, query: { ...opts.query, page, size: 200 } });
    rows.push(...batch);
    if (batch.length < 200) return rows;
  }
}

export interface BlobResult {
  blob: Blob;
  filename: string | null;
  sha256: string | null;
}

/** Binary downloads (PDFs, photos) need the bearer header, so they are fetched as blobs. */
export async function apiBlob(path: string, opts: RequestOptions = {}): Promise<BlobResult> {
  const response = await authorized(path, opts);
  if (!response.ok) throw await problemFrom(response);
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return {
    blob: await response.blob(),
    filename: match?.[1] ? decodeURIComponent(match[1]) : null,
    sha256: response.headers.get('x-content-sha256'),
  };
}

export function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
